import { Injectable } from '@angular/core';

import { DialogService } from 'dspx-core';

import { APP_DATA_KEYS } from '../constants/app-data.constants';
import { LogLevel } from '../constants/log.constants';

import { BaseMessageProgress, BaseMessageRequest, BaseMessageResult } from '../interfaces/messaging.interface';
import { ISyncLogEntry } from '../interfaces/sync-log-entry.interface';

import { DateTimeHelper } from '../helpers/date-time-helper';
import { ISpinner, SyncOperationHandler } from '../helpers/sync-operation-handler';
import { RingBuffer } from '../helpers/ring-buffer';

import { AppDataService } from './app-data.service';
import { CustomSpinnerService } from './custom-spinner.service';

@Injectable({
  providedIn: 'root'
})
export class SyncOperationService {

  private handlerRegistry: Map<string, SyncOperationHandler> = new Map();
  private syncLogBuffer = new RingBuffer<ISyncLogEntry>();

  constructor(
    private dialogService: DialogService,
    private appDataService: AppDataService,
    private customSpinnerService: CustomSpinnerService) {
  }

  registerHandler(requestSource: string): boolean {

    if (this.handlerRegistry.has(requestSource)) {
      return false;
    }
    const handler = this.createSyncOperationHandler(requestSource);
    this.handlerRegistry.set(requestSource, handler);
    return true;
  }

  unregisterHandler(requestSource: string): boolean {

    if (!this.handlerRegistry.has(requestSource)) {
      return false;
    }
    this.handlerRegistry.delete(requestSource);
    return true;
  }

  setSyncLogBufferMaxSize(newMaxBufferSize: number): void {
    this.syncLogBuffer.setMaxBufferSize(newMaxBufferSize)
  }

  async onStarted<TRequest extends BaseMessageRequest>(
    operationName: string,
    request: TRequest):
    Promise<boolean> {

    this.addSyncLogEntry(operationName, LogLevel.info, 'Operation started');
    return await this.run(request.requestSource, handler => handler.onStarted(operationName, request));
  }

  async onProgress<TRequest extends BaseMessageRequest, TProgress extends BaseMessageProgress>(
    operationName: string,
    request: TRequest,
    progress: TProgress):
    Promise<boolean> {

    this.addSyncLogEntry(operationName, progress.logLevel, progress.message, progress.details);
    return await this.run(request.requestSource, handler => handler.onProgress(operationName, request, progress));
  }

  async onEnded<TRequest extends BaseMessageRequest, TResult extends BaseMessageResult>(
    operationName: string,
    request: TRequest,
    result: TResult):
    Promise<boolean> {

    this.addSyncLogEntry(operationName, LogLevel.info, 'Operation ended');
    return await this.run(request.requestSource, handler => handler.onEnded(operationName, request, result));
  }

  getSyncLogEntries(): ISyncLogEntry[] {

    return this.syncLogBuffer.getEntries();
  }

  private createSyncOperationHandler(componentClassName: string): SyncOperationHandler {

    const logLevel = this.appDataService.getAppData<LogLevel>(APP_DATA_KEYS.logLevel);

    const spinner: ISpinner = {
      ensureIsVisible: async () => await this.customSpinnerService.ensureSpinnerIsVisible(),
      hideAll: async () => await this.customSpinnerService.hideAllSpinners(),
      setMessage: (title?: string, step?: string, subStep?: string) => this.customSpinnerService.setSpinnerElementMessage(title, step, subStep)
    };

    const onError = <TResult extends BaseMessageResult>(operationName: string, result: TResult) =>
      this.dialogService.showSimplePopup(`${operationName} failed with error: ${result.error}`, `${operationName} Error`, 'Close');

    const syncOperationHandler = new SyncOperationHandler(logLevel, componentClassName, spinner, onError);

    return syncOperationHandler;
  }

  private async run(requestSource: string, func: (handler: SyncOperationHandler) => Promise<void>): Promise<boolean> {

    if (!this.handlerRegistry.has(requestSource)) {
      return false;
    }

    const handler = this.handlerRegistry.get(requestSource);
    await func(handler);
    return true;
  }

  private addSyncLogEntry(
    operationName: string,
    logLevel: LogLevel,
    message: string,
    details: string = null):
    ISyncLogEntry {

    const syncLogEntry: ISyncLogEntry = {
      timestamp: DateTimeHelper.now().toJSDate(),
      operationName,
      logLevel,
      message,
      details
    };
    this.syncLogBuffer.add(syncLogEntry);
    return syncLogEntry;
  }
}
